import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Pencil, X } from 'lucide-react';

import { ApiError, useApi } from '../../lib/api';
import type { ApiResponse, Proposal } from '../../types';

/**
 * La demande d'une proposition, corrigible.
 *
 * Elle était figée à la création, et c'est une erreur : on découvre en lisant la
 * première version que la consigne était mal formulée, trop vague sur le
 * périmètre ou muette sur une contrainte. Il fallait alors créer une proposition
 * neuve, ce qui perdait le fil de discussion, les documents et les versions déjà
 * produites.
 *
 * La modification ne réécrit rien d'elle-même : elle vaut pour la génération
 * suivante, et le serveur le dit puisqu'il est seul à savoir s'il y a une
 * exécution en vol, celle-ci ayant lu la demande à son démarrage.
 */
export function DemandeModifiable({ proposal }: { proposal: Proposal }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [edition, setEdition] = useState(false);
    const [texte, setTexte] = useState(proposal.prompt);
    const [message, setMessage] = useState<string | null>(null);

    const enregistrer = useMutation({
        mutationFn: () => api.patch<ApiResponse<Proposal> & { message?: string }>(
            `/api/proposals/${proposal.id}`, { prompt: texte.trim() },
        ),
        onSuccess: (reponse) => {
            setEdition(false);
            setMessage(reponse.message || null);
            void queryClient.invalidateQueries({ queryKey: ['proposal', proposal.id] });
            void queryClient.invalidateQueries({
                queryKey: ['proposals', proposal.opportunityId],
            });
        },
    });

    // Le plancher est celui de la création : moins de trente caractères ne cadre
    // rien, et le refuser ici évite un aller-retour pour l'apprendre.
    const tropCourt = texte.trim().length < 30;

    if (!edition) {
        return (
            <div className="group">
                <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 whitespace-pre-line text-sm text-[var(--color-text-secondary)]">
                        {proposal.prompt}
                    </p>
                    <button
                        onClick={() => { setTexte(proposal.prompt); setEdition(true); }}
                        title="Corriger la demande"
                        className="shrink-0 rounded p-1 text-[var(--color-text-muted)] opacity-0
                            hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text)]
                            group-hover:opacity-100"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                </div>
                {message && (
                    <p className="mt-1 text-xs text-[var(--color-text-muted)]">{message}</p>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <textarea
                className="input min-h-32 text-sm"
                value={texte}
                onChange={(event) => setTexte(event.target.value)}
                autoFocus
            />
            <div className="flex items-center gap-2">
                <button
                    className="btn-primary flex items-center gap-2 text-sm"
                    disabled={tropCourt || enregistrer.isPending}
                    onClick={() => enregistrer.mutate()}
                >
                    {enregistrer.isPending
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Check className="h-4 w-4" />}
                    Enregistrer
                </button>
                <button
                    className="btn-secondary flex items-center gap-2 text-sm"
                    onClick={() => { setEdition(false); setTexte(proposal.prompt); }}
                >
                    <X className="h-4 w-4" />
                    Annuler
                </button>
                <p className="text-xs text-[var(--color-text-muted)]">
                    {tropCourt
                        ? 'Au moins trente caractères.'
                        : 'Vaudra pour la prochaine génération.'}
                </p>
            </div>
            {enregistrer.isError && (
                <p className="text-sm text-[var(--color-error)]">
                    {(enregistrer.error as ApiError).message}
                </p>
            )}
        </div>
    );
}
